import React, {useEffect, useRef, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import * as Yup from "yup";
import {makeRequired, makeValidate} from "mui-rff";
import {Col, Row} from "reactstrap";
import Form from "../../../../base/form";
import {OutlinedDropdownForm} from "../../../../base/dropdown/outlined_dropdown";
import Button from "../../../../base/button/button";
import {OutlinedInputForm} from "../../../../base/input/outlined_input";
import ModifyAvailabilityReasonsDialog from "../../../modify-availability-reasons-dialog";
import {deepEqual} from "../../../../../../core/services/utils/utils";
import Api from "../../../../../../core/services/api/api";
import useAccessLevels from "../../../../../hooks/use-access-levels";

const formNames = {
    reason: 'reason',
    note: 'note',
}

/**
 * Dummy reason, passed to the drop box for showing create new button
 * @type {{id: number, label: string, value: null}}
 */
const dummyReason = {
    id: -1,
    label: '',
    value: null,
}

const schema = Yup.object().shape({
    [formNames.reason]: Yup.object().nullable().required(),
    [formNames.note]: Yup.string().trim().nullable(),
})
const validate = makeValidate(schema);
const required = makeRequired(schema);

const ReasonNoteSection = ({formId, onSubmit, onUpdate, submitting, initialValues: initialValuesProp}) => {
    const [loading, setLoading] = useState(false);
    const [reasons, setReasons] = useState([]);
    const [initialValues, setInitialValues] = useState(initialValuesProp ?? {});
    const [modifyReasonsListDialogOpen, setModifyReasonsListDialogOpen] = useState(false);
    const isMounted = useIsMounted();
    const initialRenderRef = useRef(true);
    /** @type {React.MutableRefObject<formApi>}*/
    const formRef = useRef();
    const valueRef = useRef();
    const operatorAccessLevels = useAccessLevels();

    /**
     * After the initial render of the component, sets the initial values of the form
     */
    useEffect(() => {
        setInitialValues({
            [formNames.reason]: initialValuesProp?.reason ?? undefined,
            [formNames.note]: initialValuesProp?.note ?? undefined,
        })
    }, [initialValuesProp])

    /**
     * With initial render of the component calls the function for getting the list of reasons for availability modification
     */
    useEffect(() => {
        if (initialRenderRef.current) {
            getProductAvailabilityChangeReasons().then();
            initialRenderRef.current = false
        }
    }, [])

    /**
     * Gets the list of product availability modification reasons from the server:
     * - Calls the api regarding this action
     * - if the api call was successful, populates the [reasons] in the state
     * - Sets the selected value of the drop box to the one that is set as default
     */
    const getProductAvailabilityChangeReasons = async () => {
        setLoading(true);
        const response = await Api.getInventoryAdjustmentsReasons();
        if (!isMounted())
            return;
        setLoading(false)
        if (response?.resultFlag) {
            setReasons(response?.data ?? [])
            if (!(!!initialValues?.reason))
                setInitialValues(prevState => ({
                    ...prevState,
                    [formNames.reason]: response?.data?.find(e => e.isDefault) ?? undefined
                }))
        }
    }

    /**
     * Shows modify reason dialog by setting the [modifyReasonOpen] to true in the inner state
     * @param {MouseEvent<HTMLButtonElement>} event
     */
    const showModifyReasonDialog = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setModifyReasonsListDialogOpen(true);
    }

    /**
     * Renders the option shown in the list of drop box options
     * @param {{value: object, id: number, label: string}} item
     */
    const renderDropBoxItems = (item) => {
        // Renders the button for modifying reasons
        if (item.id === -1) {
            return <div className={'w-100 d-flex align-items-center justify-content-center'}>
                <Button
                    type={"button"}
                    variant={"contained"}
                    className={'px-5'}
                    color={'primary'}
                    onClick={(e) => showModifyReasonDialog(e)}
                >
                    Modify reason list
                </Button>
            </div>
        }
        return <div className={'modify-availability-reason-select-box-options'}>
            <p>
                {item?.value?.code} - <span>{item?.value?.title ?? ''}</span>
            </p>
        </div>
    }

    /**
     * Handles updating the list of reasons based on modification
     * If the selected item was removed in modification, sets the default reason as the selected value otherwise, empties the field
     * @param newReasons
     */
    const updateReasonsList = (newReasons) => {
        if (deepEqual(newReasons, reasons))
            return;
        setReasons(newReasons)
        const alreadySelectedValueRemoved = !(!!newReasons.find(e => e.id === valueRef.current?.[formNames.reason]?.id))
        if (alreadySelectedValueRemoved) {
            const defaultValue = newReasons.find(e => e.isDefault)
            formRef.current.change(formNames.reason, defaultValue);
            return;
        }
        formRef.current.change(formNames.reason, newReasons.find(e => e.id === valueRef.current?.[formNames.reason]?.id))
    }

    /**
     * Renders the select item value in the drop box field
     * @param item selected Option
     * @return {string}
     */
    const renderDropBoxValue = (item) => {
        return `${item?.code} - ${item?.title}`
    }

    return (
        <>
            <Form
                onSubmit={onSubmit}
                validate={validate}
                initialValues={initialValues}
                id={formId}
                render={({values, form}) => {
                    formRef.current = form;
                    valueRef.current = values;
                    return (
                        <Row className='no-gutters align-items-center justify-content-end mt-4'>
                            <Col xs={12} className={'mb-3'}>
                                <OutlinedDropdownForm
                                    label={'Reason'}
                                    name={formNames.reason}
                                    renderValue={renderDropBoxValue}
                                    renderOptions={renderDropBoxItems}
                                    required={!!required[formNames.reason]}
                                    onBlur={(e) => onUpdate?.({
                                        [formNames.reason]: e.target.value,
                                        [formNames.note]: values[formNames.note] ?? ''
                                    })}
                                    data={[
                                        ...(reasons.map(e => ({
                                            value: e,
                                            label: e?.code,
                                            id: e.id
                                        })) ?? []),
                                        ...(operatorAccessLevels.packages.bizInventory.manageAdjustmentReasons ? [dummyReason] : [])
                                    ]}
                                />
                            </Col>
                            <Col xs={12} className={'mb-3'}>
                                <OutlinedInputForm
                                    multiline={true}
                                    maxRows={6}
                                    minRows={6}
                                    disabled={submitting || loading}
                                    label={'Note (optional)'}
                                    name={formNames.note}
                                    required={!!required[formNames.note]}
                                    value={values[formNames.note] ?? ''}
                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                    onBlur={(e) => onUpdate?.({
                                        [formNames.reason]: values[formNames.reason],
                                        [formNames.note]: e.target.value
                                    })}
                                />
                            </Col>
                        </Row>
                    );
                }}
            />
            <ModifyAvailabilityReasonsDialog
                setOpen={setModifyReasonsListDialogOpen}
                open={modifyReasonsListDialogOpen}
                allReasons={reasons}
                onDialogClosed={updateReasonsList}
            />
        </>
    );
}

export default ReasonNoteSection;
